/**
 * Erlaubte Rollen-IDs für Selbstregistrierung / Self-Access-Requests (keine direkte Rechtevergabe).
 * Abgleich mit Feature-Auswahl / Onboarding.
 */

function __asStr(v) {
  return String(v ?? "").trim();
}

/** Rollen, die niemals über Self-Signup-Payload akzeptiert werden. */
const FORBIDDEN_SELF_SIGNUP_ROLES = new Set(["superadmin", "admin", "owner"]);

/** Positivliste für Self-Service Access Requests (client + server). */
export const SELF_SIGNUP_ALLOWED_ROLE_IDS = [
  "trainer",
  "trainer_coordinator",
  "jugendwart",
  "sportwart",
  "vorstand",
  "vereinsadmin",
  "kassenwart",
  "event_organizer",
  "team_captain",
  "player",
  "parent",
];

const ALLOWED_SET = new Set(SELF_SIGNUP_ALLOWED_ROLE_IDS.map((x) => __asStr(x).toLowerCase()));

/** UI-Rolle Registrierung: Vereinsfunktionär (wird beim Speichern expandiert). */
export const SELF_SIGNUP_REGISTER_UI_ROLE_CLUB_OFFICIAL = "club_official";

/** Vereinsfunktionär → vorerst kombinierte Berechtigungsanfrage (Jugendwart, Vorstand, Vereinsadmin). */
export const CLUB_OFFICIAL_SELF_SIGNUP_EXPANDED_ROLE_IDS = ["jugendwart", "vorstand", "vereinsadmin"];

/** Vier Karten im Registrierungs-Wizard (keine Feinrollen). */
export const SELF_SIGNUP_REGISTER_ROLE_OPTIONS = [
  { role: "trainer", labelKey: "onboarding.roles.trainer" },
  { role: SELF_SIGNUP_REGISTER_UI_ROLE_CLUB_OFFICIAL, labelKey: "onboarding.roles.clubOfficial" },
  { role: "player", labelKey: "onboarding.roles.player" },
  { role: "parent", labelKey: "onboarding.roles.parent" },
];

/** Labels über i18n keys der onboarding.roles.* (Marketplace, Feature-Auswahl, Status). */
export const SELF_SIGNUP_ROLE_OPTIONS = [
  { role: "trainer", labelKey: "onboarding.roles.trainer" },
  { role: "trainer_coordinator", labelKey: "onboarding.roles.trainerCoordinator" },
  { role: "jugendwart", labelKey: "onboarding.roles.jugendwart" },
  { role: "sportwart", labelKey: "onboarding.roles.sportwart" },
  { role: "vorstand", labelKey: "onboarding.roles.vorstand" },
  { role: "vereinsadmin", labelKey: "onboarding.roles.vereinsadmin" },
  { role: "kassenwart", labelKey: "onboarding.roles.kassenwart" },
  { role: "event_organizer", labelKey: "onboarding.roles.eventOrganizer" },
  { role: "team_captain", labelKey: "onboarding.roles.teamCaptain" },
  { role: "player", labelKey: "onboarding.roles.player" },
  { role: "parent", labelKey: "onboarding.roles.parent" },
];

const REGISTER_UI_ROLE_EXPAND = {
  [SELF_SIGNUP_REGISTER_UI_ROLE_CLUB_OFFICIAL]: CLUB_OFFICIAL_SELF_SIGNUP_EXPANDED_ROLE_IDS,
};

/** @param {unknown} roleId */
export function getSelfSignupRoleLabelKey(roleId) {
  const x = __asStr(roleId).toLowerCase();
  if (!x) return "";
  const reg = SELF_SIGNUP_REGISTER_ROLE_OPTIONS.find((o) => o.role === x);
  if (reg) return reg.labelKey;
  const opt = SELF_SIGNUP_ROLE_OPTIONS.find((o) => o.role === x);
  return opt ? opt.labelKey : "";
}

/**
 * Filtert angefragte Rollen auf die Positivliste (Tamper-Schutz).
 * @param {unknown[]} roles
 * @returns {string[]}
 */
/** Koordinations-Antwort, wenn Rolle trainer_coordinator gewählt ist (Frage entfällt). */
export const TRAINER_COORDINATION_IMPLICIT_VALUE = "area";

function __normalizeRoleList(roles) {
  if (roles instanceof Set) return Array.from(roles);
  return Array.isArray(roles) ? roles : [];
}

/** @param {unknown} roles */
export function selectedRolesIncludeTrainerCoordinator(roles) {
  return __normalizeRoleList(roles).some((r) => __asStr(r).toLowerCase() === "trainer_coordinator");
}

/** @param {unknown} roles */
export function shouldAskTrainerCoordinationQuestion(roles) {
  return !selectedRolesIncludeTrainerCoordinator(roles);
}

const INDEPENDENT_TRAINER_SELF_SIGNUP_ROLES = new Set(["trainer", "trainer_coordinator"]);

/** Unabhängiger Trainer ohne Verein — nur bei Trainer / Trainerkoordinator. */
export function selectedRolesAllowIndependentTrainer(roles) {
  const list = __normalizeRoleList(roles).map((r) => __asStr(r).toLowerCase());
  if (!list.length) return false;
  return list.every((r) => INDEPENDENT_TRAINER_SELF_SIGNUP_ROLES.has(r));
}

/**
 * @param {unknown} roles
 * @param {unknown} userChoice – none | single | multiple | area
 */
export function resolveTrainerCoordinationForRoles(roles, userChoice) {
  if (selectedRolesIncludeTrainerCoordinator(roles)) {
    return TRAINER_COORDINATION_IMPLICIT_VALUE;
  }
  const c = __asStr(userChoice).toLowerCase();
  if (c === "none" || c === "single" || c === "multiple" || c === "area") return c;
  return "none";
}

export function sanitizeSelfAccessRequestedRoles(roles) {
  const list = Array.isArray(roles) ? roles : [];
  const out = [];
  const seen = new Set();
  for (const r of list) {
    const x = __asStr(r).toLowerCase();
    if (!x || FORBIDDEN_SELF_SIGNUP_ROLES.has(x)) continue;
    if (!ALLOWED_SET.has(x)) continue;
    if (seen.has(x)) continue;
    seen.add(x);
    out.push(x);
  }
  return out;
}

/**
 * Mappt UI-Rollen (z. B. club_official) auf persistierte Rollen-IDs, dann Positivliste.
 * @param {unknown[]} roles
 * @returns {string[]}
 */
export function expandSelfSignupUiRolesForPersist(roles) {
  const list = __normalizeRoleList(roles);
  const expanded = [];
  const seen = new Set();
  for (const r of list) {
    const x = __asStr(r).toLowerCase();
    if (!x) continue;
    const mapped = REGISTER_UI_ROLE_EXPAND[x];
    if (mapped) {
      for (const id of mapped) {
        const y = __asStr(id).toLowerCase();
        if (y && !seen.has(y)) {
          seen.add(y);
          expanded.push(y);
        }
      }
      continue;
    }
    if (!seen.has(x)) {
      seen.add(x);
      expanded.push(x);
    }
  }
  return sanitizeSelfAccessRequestedRoles(expanded);
}
